import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CalendarDays, DollarSign, Clock } from "lucide-react";
import { format } from "date-fns";

const HOUR_LABELS = Array.from({ length: 17 }, (_, i) => {
  const h = i + 6;
  return `${h.toString().padStart(2, "0")}:00`;
});

const ACTIVITY_BG = {
  basketball: "bg-orange-200 border-orange-400 text-orange-900",
  volleyball: "bg-yellow-200 border-yellow-400 text-yellow-900",
  badminton: "bg-green-200 border-green-400 text-green-900",
  tennis: "bg-lime-200 border-lime-400 text-lime-900",
  zumba: "bg-pink-200 border-pink-400 text-pink-900",
  taekwondo: "bg-red-200 border-red-400 text-red-900",
  basketball_3x3: "bg-amber-200 border-amber-400 text-amber-900",
  dance: "bg-violet-200 border-violet-400 text-violet-900",
  yoga: "bg-purple-200 border-purple-400 text-purple-900",
};

export default function VenueSchedule() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [venue, setVenue] = useState(null);

  useEffect(() => {
    api.get(`/venues/${venueId}`).then(r => setVenue(r.data)).catch(() => {});
  }, [venueId]);

  useEffect(() => {
    setLoading(true);
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    api.get(`/space-engine/venues/${venueId}/schedule`, { params: { date: dateStr } })
      .then(r => { setSchedule(r.data); setLoading(false); })
      .catch(() => { setSchedule(null); setLoading(false); });
  }, [venueId, selectedDate]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight" data-testid="page-title">Venue Schedule</h1>
          <p className="text-sm text-muted-foreground">{venue?.name}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="space-y-4">
          <Calendar mode="single" selected={selectedDate} onSelect={d => d && setSelectedDate(d)} className="border-2 border-slate-200 p-3" data-testid="schedule-calendar" />
          {schedule && (
            <Card>
              <CardContent className="py-4 space-y-2">
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{format(selectedDate, "EEEE, MMM d")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">{schedule.total_reservations} reservations</span>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-bold text-green-700">PHP {schedule.total_revenue?.toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="lg:col-span-3">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : !schedule?.schedule?.length ? (
            <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
              <p className="text-muted-foreground font-heading">No spaces configured</p>
              <p className="text-sm text-muted-foreground mt-1">Set up spaces in Space Configuration first</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[600px]">
                <div className="grid gap-2 mb-2" style={{ gridTemplateColumns: `80px repeat(${schedule.schedule.length}, 1fr)` }}>
                  <div className="text-xs font-heading uppercase text-muted-foreground">Time</div>
                  {schedule.schedule.map(s => (
                    <div key={s.space.physical_space_id} className="text-xs font-heading uppercase font-bold truncate" title={s.space.space_name}>
                      {s.space.space_name}
                    </div>
                  ))}
                </div>

                {HOUR_LABELS.map(hour => {
                  const dateStr = format(selectedDate, "yyyy-MM-dd");
                  const slotStart = `${dateStr}T${hour}:00`;

                  return (
                    <div key={hour} className="grid gap-2 mb-1" style={{ gridTemplateColumns: `80px repeat(${schedule.schedule.length}, 1fr)` }}>
                      <div className="text-xs text-muted-foreground py-2 font-mono">{hour}</div>
                      {schedule.schedule.map(s => {
                        const reservation = s.reservations.find(r =>
                          r.reservation_start_at <= slotStart && r.reservation_end_at > slotStart
                        );
                        if (reservation) {
                          const cfg = s.configs?.find(c => c.activity_configuration_id === reservation.activity_configuration_id);
                          const activityTypeId = cfg?.activity_type_id || "";
                          return (
                            <div key={s.space.physical_space_id}
                              className={`rounded px-2 py-1.5 text-[10px] border ${ACTIVITY_BG[activityTypeId] || 'bg-blue-100 border-blue-300 text-blue-900'}`}
                              data-testid={`schedule-res-${reservation.booking_reservation_id}`}>
                              <div className="font-bold truncate">{reservation.activity_name || cfg?.configuration_name || "Reserved"}</div>
                              <div className="truncate">{reservation.user_name || reservation.source_type}</div>
                            </div>
                          );
                        }
                        return (
                          <div key={s.space.physical_space_id} className="rounded border border-dashed border-slate-200 py-1.5 px-2 text-[10px] text-slate-300">
                            Available
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
